/**
 * Board-tab card geometry and default arrangement, kept out of the component so
 * it is unit-testable and SheetsCanvas.tsx exports only a component (the repo's
 * catalogRowOrder.ts / matchTone.ts pattern).
 */
import type { ConnectedSource } from './connectedSources'

/**
 * Card footprint in canvas coordinates. Landscape, because a P&ID is — the
 * design's cards are wider than tall and the thumbnail fills them edge to edge.
 */
export const CARD_WIDTH = 190
export const CARD_HEIGHT = 135
const CARD_GAP_X = 200

/**
 * Vertical space the floating title chip and enlarge control occupy ABOVE a
 * card — they render at `-top-9` in SheetsCanvas, so 36px.
 *
 * The gap below has to clear it. At the previous 34px it did not: selecting a
 * card in the fan put its filename chip 2px INTO the bottom edge of the card
 * above, and its shadow further still.
 */
export const CARD_LABEL_HEIGHT = 36

/** Clear the chip, plus enough that it reads as floating rather than touching. */
const CARD_GAP_Y = CARD_LABEL_HEIGHT + 12
export const ORIGIN_X = 60
export const ORIGIN_Y = 60

/**
 * Cards per column before the fan starts a new one.
 *
 * The design draws the connected sources as a single column beside the anchor,
 * which is the right picture for the four-sheet tag it was drawn against. It is
 * the wrong picture for a tag with 32 connected documents: one column becomes
 * ~5,400px tall, so all but the first few are below the fold on a canvas whose
 * only affordance for reaching them is scrolling past empty space. Wrapping
 * keeps the design's shape for small fans and keeps a large one on screen.
 */
/**
 * Cards per column before the fan wraps to the next one.
 *
 * FOUR, not the design's five, because of what fits on screen. The canvas below
 * the toolbar is ~783px tall on a 900px display (1440x900 is the design's own
 * frame). A row is CARD_HEIGHT + CARD_GAP_Y = 183px, so with five rows the fifth
 * card starts at 60 + 4 x 183 = 792px — entirely below the fold, reachable only
 * by scrolling a canvas that gives no sign there is more. That is how a tag with
 * eight sources showed SEVEN cards on the Board while the Knowledge graph drew
 * all eight, and the two tabs looked inconsistent when they were reading the
 * same list. Four rows end at 60 + 3 x 183 + 135 = 744px, inside the viewport;
 * the fan grows sideways instead, where a scrollbar is expected.
 */
const COLUMN_ROWS = 4

/** Canvas height available under the header + toolbar on a 900px display. */
export const VIEWPORT_900_CANVAS_HEIGHT = 900 - 60 - 57

export interface CardPosition {
  x: number
  y: number
}

/**
 * Fan layout: the first source on the left, the rest in columns to its right,
 * which is how the design arranges a tag's sources and what the connector lines
 * are drawn against.
 *
 * Keyed by `ConnectedSource.id`, NOT by document id: one drawing that prints
 * the tag in several places is several sources, and keying by document collapsed
 * them onto one position where they drew as a single card.
 */
export const defaultLayout = (sources: ConnectedSource[]): Record<string, CardPosition> => {
  const layout: Record<string, CardPosition> = {}
  sources.forEach((source, i) => {
    if (i === 0) {
      layout[source.id] = { x: ORIGIN_X, y: ORIGIN_Y + (CARD_HEIGHT + CARD_GAP_Y) }
      return
    }
    const slot = i - 1
    const column = Math.floor(slot / COLUMN_ROWS)
    const row = slot % COLUMN_ROWS
    layout[source.id] = {
      x: ORIGIN_X + CARD_GAP_X + CARD_WIDTH + column * (CARD_WIDTH + CARD_GAP_X),
      y: ORIGIN_Y + row * (CARD_HEIGHT + CARD_GAP_Y),
    }
  })
  return layout
}

/**
 * The zoom that shows the WHOLE board inside a viewport — never above 1, so
 * "fit" brings far cards into view rather than blowing a two-card board up
 * into a blur. A viewport with no size yet (first paint, jsdom) fits at 1.
 */
export const fitScale = (
  viewport: { width: number; height: number },
  canvas: { width: number; height: number },
  padding = 24,
): number => {
  if (viewport.width <= 0 || viewport.height <= 0 || canvas.width <= 0 || canvas.height <= 0) return 1
  return Math.min(
    1,
    (viewport.width - padding) / canvas.width,
    (viewport.height - padding) / canvas.height,
  )
}
